import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { DashboardStatsService } from '../../services/dashboard-stats.service';
import { FieldExecutionStatsService } from '../../services/field-execution-stats.service';
import { DepartmentSchemesService } from '../../services/department-schemes.service';
import { UserProfileDisplayService } from '../../services/user-profile-display.service';
import { Project } from '../../models/project.model';

type HmListFilter = 'hmDelayed' | 'hmPending' | 'lowProgress';
type FeListFilter = 'feAll' | 'feSanctioned' | 'feProgress' | 'feDelayed' | 'feCompleted';
type HomeListFilter = HmListFilter | FeListFilter;

@Component({
    selector: 'app-home',
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
    private router = inject(Router);
    private authService = inject(AuthService);
    private firestoreService = inject(FirestoreProjectService);
    /** Top ribbon — HM Decision Dashboard only. */
    readonly hmStats$ = inject(DashboardStatsService);
    /** Bottom strip — Field Execution only. */
    readonly feStats$ = inject(FieldExecutionStatsService);
    private deptService = inject(DepartmentSchemesService);
    readonly profileUi = inject(UserProfileDisplayService);
    private destroy$ = new Subject<void>();

    currentUser = this.authService.currentUser;
    isAuthenticated = this.authService.isAuthenticated;

    showLogoutModal = signal(false);
    showCriticalModal = signal(false);
    /** HM Decision projects — used for critical alerts and HM list modals only. */
    hmProjects = signal<Project[]>([]);

    listFilter = signal<HomeListFilter | null>(null);

    listIsFieldExecution = computed(() => {
        const f = this.listFilter();
        return f === 'feAll' || f === 'feSanctioned' || f === 'feProgress' || f === 'feDelayed' || f === 'feCompleted';
    });

    listTitle = computed(() => {
        switch (this.listFilter()) {
            case 'hmDelayed':   return 'HM Delayed Projects';
            case 'hmPending':   return 'HM Pending Projects';
            case 'lowProgress': return 'HM Low Progress Projects (<30%)';
            case 'feDelayed':   return 'Field Execution Delayed Projects';
            case 'feAll':       return 'Field Execution Projects';
            case 'feCompleted': return 'Field Execution Completed Projects';
            case 'feSanctioned': return 'Field Execution Projects by Sanctioned Amount';
            case 'feProgress':  return 'Field Execution Projects by Progress';
            default:            return 'Projects';
        }
    });

    listProjects = computed<Project[]>(() => {
        switch (this.listFilter()) {
            case 'hmDelayed':   return this.hmStats$.delayedProjects();
            case 'hmPending':   return this.hmStats$.hmPendingProjects();
            case 'lowProgress': return this.hmStats$.lowProgressProjects();
            case 'feDelayed':   return this.feStats$.delayedProjects();
            case 'feCompleted': return this.feStats$.completedProjects();
            case 'feAll':
            case 'feSanctioned':
            case 'feProgress':  return this.feStats$.progressProjects();
            default:            return [];
        }
    });

    criticalProjects = computed<Project[]>(() => {
        const today = Date.now();
        const seen = new Set<string>();
        return this.hmProjects().filter(p => {
            if (p.status === 'Completed') return false;
            const isStuck     = p.status === 'Stuck';
            const isRecurring = !!p.recurringIntervention;
            const isOsdDelayed = !!p.isDelayed;
            const targetDate  = p.targetDate || p.end;
            const isOverdue   = targetDate
                ? Math.floor((today - new Date(targetDate).getTime()) / 86400000) > 60
                : false;
            if (!(isStuck || isRecurring || isOsdDelayed || isOverdue)) return false;
            if (seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
        });
    });

    criticalReason(p: Project): string {
        const tags: string[] = [];
        if (!!p.recurringIntervention) tags.push('Recurring');
        if (p.status === 'Stuck')      tags.push('Stuck');
        const t = p.targetDate || p.end;
        if (t && Math.floor((Date.now() - new Date(t).getTime()) / 86400000) > 60) tags.push('Overdue');
        return tags.join(' · ');
    }

    departmentDisplayName(proj: Project): string {
        const raw: any = proj as any;
        const candidates = [
            proj.dept,
            raw.department,
            raw.departmentName,
            raw.deptName,
            raw.deptId,
            raw.departmentId,
        ]
            .map((v: unknown) => (v == null ? '' : String(v).trim()))
            .filter((s: string) => s.length > 0);

        if (candidates.length === 0) {
            const fromScheme = this.departmentNameFromScheme(proj.scheme);
            return fromScheme ?? '—';
        }

        const all = this.deptService.getDepartments();
        const norm = (s: string) =>
            s
                .toLowerCase()
                .replace(/[\s&]/g, '_')
                .replace(/[^a-z0-9_]/g, '')
                .replace(/_+/g, '_');

        for (const c of candidates) {
            const entry = this.deptService.getDepartmentByName(c);
            if (entry) {
                return entry.name;
            }
            const key = norm(c);
            const bySlug =
                all.find(d => norm(d.name) === key) ||
                all.find(d => norm(d.shortName) === key);
            if (bySlug) {
                return bySlug.name;
            }
        }

        const fromScheme = this.departmentNameFromScheme(proj.scheme);
        if (fromScheme) {
            return fromScheme;
        }

        return candidates[0];
    }

    fieldExecutionLocation(proj: Project): string {
        const block = (proj.block || '').trim();
        const village = (proj.village || proj.loc || '').trim();
        if (block && village) return `${block} · ${village}`;
        return block || village || '—';
    }

    private departmentNameFromScheme(scheme: string | undefined): string | undefined {
        const s = (scheme ?? '').trim();
        if (!s) {
            return undefined;
        }
        const all = this.deptService.getDepartments();
        const sl = s.toLowerCase();
        for (const d of all) {
            const card = d.schemes.find(c => c.name === s || c.name.toLowerCase() === sl);
            if (card) {
                return d.name;
            }
        }
        for (const d of all) {
            const card = d.schemes.find(
                c => c.name.length >= 6 && (s.includes(c.name) || c.name.includes(s))
            );
            if (card) {
                return d.name;
            }
        }
        return undefined;
    }

    openHmList(filter: HmListFilter): void {
        this.listFilter.set(filter);
    }

    openFeList(filter: FeListFilter): void {
        this.listFilter.set(filter);
    }

    closeList(): void {
        this.listFilter.set(null);
    }

    ngOnInit(): void {
        this.firestoreService.getHmProjects$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: projects => {
                    this.hmProjects.set(projects);
                },
                error: err => {
                    console.error('[HomeComponent] Error loading HM projects:', err);
                    setTimeout(() => {
                        this.firestoreService.getHmProjects$()
                            .pipe(takeUntil(this.destroy$))
                            .subscribe(projects => this.hmProjects.set(projects));
                    }, 1000);
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openProject(id: string): void {
        const fromFieldExecution = this.listIsFieldExecution();
        this.listFilter.set(null);
        this.showCriticalModal.set(false);

        if (fromFieldExecution) {
            const proj = this.feStats$.projects().find(p => p.id === id);
            if (proj?.block) {
                this.router.navigate(['/field-execution', proj.block]);
                return;
            }
            this.router.navigate(['/field-execution']);
            return;
        }

        this.router.navigate(['/dhamnagar-dashboard/project', id]);
    }

    navigateToDashboard(): void {
        this.router.navigate(['/dhamnagar-dashboard']);
    }

    navigateToFieldExecution(): void {
        this.router.navigate(['/field-execution']);
    }

    /** Mobile summary: show Crores when amount ≥ 100 Lakh. */
    formatFeSanctioned(): string {
        const lakhs = this.feStats$.sanctionedAmount();
        if (!lakhs) return '₹0';
        if (lakhs >= 100) {
            const cr = lakhs / 100;
            return '₹' + (Number.isInteger(cr) ? cr : cr.toFixed(2)) + 'Cr';
        }
        return '₹' + Math.round(lakhs).toLocaleString('en-IN') + 'L';
    }

    async logout(): Promise<void> {
        await this.authService.logout();
    }
}
